import React, { useRef, useState } from 'react';
import { uploadAndUpdateAvatar, getUserById } from 'services/profile.service';
import { useAuth } from 'context/AuthContext';
import './Hero.scss';

interface HeroProps {
  user: {
    nome: string;
    primeiraLetra: string;
    foto: string;
  };
  badges: Array<{
    id: number;
    nome: string;
    imagem: string;
    conquistado: boolean;
  }>;
  ilustracao: string;
  onOpenOnboarding?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ user, badges, ilustracao, onOpenOnboarding }) => {
  const { user: authUser, refreshUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const conquistedBadges = badges.filter(b => b.conquistado).slice(0, 3);

  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !authUser?._id) return;

    // Valida tipo de arquivo
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    // Valida tamanho do arquivo (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB em bytes
    if (file.size > maxSize) {
      alert('A imagem deve ter no máximo 5MB.');
      return;
    }

    try {
      setIsUploading(true);

      // Usa a função uploadAndUpdateAvatar que já faz tudo
      await uploadAndUpdateAvatar(authUser._id, file);

      // Busca os dados atualizados do usuário com GET
      const updatedUser = await getUserById(authUser._id);
      
      // Recarrega a página para exibir a nova foto
      window.location.reload();
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      alert('Erro ao atualizar foto de perfil. Tente novamente.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="hero">
      <div className="hero__content">
        <div className="hero__user-section">
          <div className="hero__avatar-container">
            <div className="hero__avatar">
              {user.foto ? (
                <img src={user.foto} alt={user.nome} />
              ) : (
                <span className="hero__avatar-letter">{user.primeiraLetra}</span>
              )}
            </div>
            <div className="hero__trophy-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" 
                  fill="#FDD835"
                  stroke="#F9A825"
                  strokeWidth="1"
                />
              </svg>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <button 
              className="hero__edit-photo"
              onClick={handleEditPhotoClick}
              disabled={isUploading}
              style={{ opacity: isUploading ? 0.6 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
            >
              {isUploading ? 'Enviando...' : 'Editar imagem'}
            </button>
          </div>

          <div className="hero__content-wrapper">
            <div className="hero__text">
              <h1 className="hero__greeting">
                Olá, <span className="hero__name">{user.nome}</span>
              </h1>
              <p className="hero__subtitle">
                Bem-vindo(a) à sua jornada<br />
                no <span className="hero__highlight">Prêmio BB 2026</span>
              </p>
            </div>

            {conquistedBadges.length > 0 && (
              <div className="hero__badges">
                {conquistedBadges.map((badge) => (
                  <button
                    key={badge.id}
                    className="hero__badge"
                  >
                    <img src={badge.imagem} alt={badge.nome} />
                  </button>
                ))}
                <a href="#badges" className="hero__ver-badges">
                  ver mais badges
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="hero__illustration">
          <div className="hero__illustration-container">
            <img 
              src={ilustracao} 
              alt="Ilustração espacial"
              className="hero__main-image"
            />
            
            {/* Elementos decorativos flutuantes */}
            <div className="hero__float-element hero__float-element--1">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="15" fill="#FDD835" opacity="0.3"/>
                <circle cx="20" cy="20" r="10" fill="#FDD835" opacity="0.6"/>
                <circle cx="20" cy="20" r="5" fill="#FDD835"/>
              </svg>
            </div>

            <div className="hero__float-element hero__float-element--2">
              <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
                <path 
                  d="M15 5L17 13L25 15L17 17L15 25L13 17L5 15L13 13L15 5Z" 
                  fill="#FFFFFF" 
                  opacity="0.8"
                />
              </svg>
            </div>

            <div className="hero__float-element hero__float-element--3">
              <div className="hero__circle hero__circle--yellow"></div>
            </div>

            <div className="hero__float-element hero__float-element--4">
              <div className="hero__circle hero__circle--white"></div>
            </div>

            <button className="hero__tutorial-button" onClick={onOpenOnboarding}>
              VER TUTORIAL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};